import { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../auth/AuthContext";
import { getAccessToken } from "../api/token";
import { getChatSocket, disconnectChatSocket } from "../lib/socket";
import type { Socket } from "socket.io-client";

export function useChatSocket() {
  const { seller } = useAuth();
  const token = getAccessToken();
  const [connected, setConnected] = useState(false);
  const [socket, setSocket] = useState<Socket | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const joinedRooms = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!seller || !token) {
      disconnectChatSocket();
      setConnected(false);
      setSocket(null);
      setOnlineUsers(new Set());
      return;
    }

    const s = getChatSocket(token);

    const onConnect = () => {
      setConnected(true);
      for (const roomId of joinedRooms.current) {
        s.emit("join:conversation", { conversationId: roomId });
      }
    };

    const onDisconnect = () => setConnected(false);

    const onPresence = (data: { accountId: string; online: boolean }) => {
      setOnlineUsers((prev) => {
        const next = new Set(prev);
        if (data.online) next.add(data.accountId);
        else next.delete(data.accountId);
        return next;
      });
    };

    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    s.on("presence:update", onPresence);

    if (!s.connected) {
      s.connect();
    } else {
      setConnected(true);
    }

    setSocket(s);

    return () => {
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
      s.off("presence:update", onPresence);
    };
  }, [seller?.accountId, token]);

  const joinConversation = useCallback((conversationId: string) => {
    joinedRooms.current.add(conversationId);
    socket?.emit("join:conversation", { conversationId });
  }, [socket]);

  const leaveConversation = useCallback((conversationId: string) => {
    joinedRooms.current.delete(conversationId);
    socket?.emit("leave:conversation", { conversationId });
  }, [socket]);

  const sendMessage = useCallback((payload: { conversationId: string; content?: string; mediaUrl?: string }) => {
    socket?.emit("message:send", payload);
  }, [socket]);

  const markRead = useCallback((conversationId: string) => {
    socket?.emit("message:read", { conversationId });
  }, [socket]);

  const startTyping = useCallback((conversationId: string) => {
    socket?.emit("typing:start", { conversationId });
  }, [socket]);

  const stopTyping = useCallback((conversationId: string) => {
    socket?.emit("typing:stop", { conversationId });
  }, [socket]);

  const isOnline = useCallback((accountId: string) => onlineUsers.has(accountId), [onlineUsers]);

  return {
    connected,
    socket,
    onlineUsers,
    joinConversation,
    leaveConversation,
    sendMessage,
    markRead,
    startTyping,
    stopTyping,
    isOnline,
  };
}
